import { ProductEntity } from '@wg/wows-commerce/core/entity/product.entity';
import type { IOpenPortByItem } from '~/settings/port';

export type BUNDLE_DESCRIPTIONS_TYPE = Pick<IBundle, 'description' | 'descriptionAdditional'>;

export enum BUNDLE_TYPES {
    default = 'default',
    random = 'random',
    serial = 'serial',
    promo = 'promo',
    periodic = 'periodic',
    mystery = 'mystery',
    forecast = 'forecast',
    progression_reward = 'progression_reward',
}

export enum BUNDLE_DECORATION {
    ADMIRAL_PACK = 'admiral_pack',
    COMMANDER_PACK = 'commander_pack',
    SIMPLE_PACK = 'simple_pack',
    WITHOUT_GLOW_ANIMATION = 'without_glow_animation',
    SEPIA_EFFECT = 'sepia_effect',
    RANDOM_BIG_ITEM = 'random_big_item',
    GRAYSCALE = 'grayscale',
    SCALE_IMAGE = 'scale_image',
    ANIMATE_BORDER = 'animate_border',
    SCROLLABLE_VIEW = 'scrollable_view',
    FULLSCREEN_WITHOUT_GALLERY = 'fullscreen_without_gallery',
    SCALE_ON_HOVER = 'scale_on_hover',
    HIDE_GALLERY_DEFAULT_BACKGROUND = 'hide_gallery_default_background',
    DISABLE_BUNDLE_HOVER_BACKGROUND = 'disable_bundle_hover_background',
    DISABLE_BUNDLE_DEFAULT_BACKGROUND = 'disable_bundle_default_background',
    BUNDLE_BIG_ICON = 'bundle_big_icon',
    TURN_OFF_CATEGORY_VIDEO = 'turn_off_category_video',
    HIDE_BUNDLE_IMAGE = 'hide_bundle_image',
    SNOWFLAKES_SHINY_ANIMATION = 'snowflakes_shiny_animation',
    DISABLE_CATEGORY_VIDEO_BACK = 'disable_category_video_back',
    FADER_LTR_GRADIENT = 'fader_ltr_gradient',
    DISCLAIMER_APPEARANCE_AND_DESCRIPTION = 'disclaimer_appearance_and_description',
    BUNDLE_CARD_CONTENT_UNDERLAY = 'bundle_card_content_underlay',
    HIDE_PURCHASE_RESULT = 'hide_purchase_result',
    PURCHASE_AS_REWARD = 'purchase_as_reward',
    SHOW_BUNDLE_KIT = 'show_bundle_kit',
    KALAM_FONTS = 'kalam_fonts',
    NY26_TIMER = 'ny26_timer',
}

export enum ILootboxRewardGroupDisplay {
    default = 'default',
    grouped = 'grouped',
}

export enum CommonRewardType {
    SHIP = 'ship',
    CAMO = 'camo',
    CREW = 'crew',
}

export enum PreviewSettingsTemplate {
    default = 'default',
}

export enum PreviewRewardCustomTypes {
    SHIP = 'ship',
    SHIP_WITH_SPECIAL_CREW = 'ship_with_special_crew',
    COAL = 'coal',
    STEEL = 'steel',
    SIGNAL = 'signal',
    CREDITS = 'credits',
    GOLD = 'gold',
    SPECIAL_SIGNAL = 'special_signal',
    ECO_SIGNAL = 'eco_signal',
    BATTLE_SIGNAL = 'battle_signal',
    PERMANENT_CAMO = 'permanent_camo',
    CAMO = 'camo',
    FREE_XP = 'free_xp',
    UNIQUE_CREW = 'unique_crew',
    ELITE_XP = 'elite_xp',
    SPECIAL_SIGNAL_WITH_CAMO = 'special_signal_with_camo',
    ALBUM_COLLECTION = 'collection_album',
    CUSTOM = 'custom',
    WOWS_PREMIUM = 'wows_premium',
}

export enum BundlePurchaseTypes {
    INGAME_CURRENCY = 'ingame_currency',
    REAL_CURRENCY = 'real_currency',
}

export enum IBundleEntityTags {
    progression_reward = 'progression_reward',
}

export enum BundleRestrictInteractionType {
    none = 'none',
    partial = 'partial',
    partial_with_price = 'partial_with_price',
    full_with_price = 'full_with_price',
    full = 'full',
}

export enum UserPrerequisiteTags {
    minor = 'minor',
    restrict_buying = 'restrict_buying',
    restrict_interaction = 'restrict_interaction',
}

export enum UserPrerequisiteCondition {
    datetime = 'datetime',
    token = 'token',
}

declare global {
    interface IBundleEntity extends IItemCommonData {
        allowCompensation: boolean;
        isBonus: boolean;
        isPrimary: boolean;
        tags: IBundleEntityTags[];
    }

    type IBundleList = Record<string, IBundle>;

    interface IPrimaryItemVehicleData {
        class?: string;
        level?: number;
        nation?: string;
        isPremium?: boolean;
        isSpecial?: boolean;
    }

    interface IPrimaryItemCustomisation {
        class?: string[];
        crewLevel?: number[];
        isPremium?: boolean[];
        isSpecial?: boolean[];
        level?: number[];
        tier?: number[];
        nation?: string[];
        type?: string;
        points?: number;
        itemSubType?: string;
        vehicleId?: number;
        shipId?: number;
        shipIds?: number[];
        currency?: string[];
        vehicle?: IPrimaryItemVehicleData;
    }

    interface DefaultBundleIcons {
        default: string;
        small: string;
        medium: string;
        big: string;
        innerBackground: string;
    }

    interface RandomBundleChildIcons extends DefaultBundleIcons {
        randomShinyImage?: null;
        randomShinyBackground?: null;
    }

    interface BundleIcons extends DefaultBundleIcons {
        mobileInnerBackground: string;
        confirmationBackground: string;
        groupingIdleImage?: string;
        groupingHoverImage?: string;
    }

    interface RandomBundleIcons extends BundleIcons {
        randomDoorLeft: string;
        randomDoorRight: string;
        randomCardBackground: string;
        randomShinyImage: string;
        randomShinyBackground: string;
        randomPartialPurchaseImage: string;
    }

    interface IRandomBundleChild {
        icons: RandomBundleChildIcons;
        id: number;
        limitedQuantity: Nullable<IBundleLimitedQuantity>;
        allowCompensation: boolean;
        title: string;
        description: string;
        descriptionAdditional?: string;
        isShiny: boolean;
        entitlements: IBundleEntity[];
        autoDescriptionSettings?: IAutoDescriptionSettings;
        backgroundColor?: string;
    }

    type IBundlePricesInfo = Partial<Record<ICategoryList, Record<string, IRange>>>;

    interface IBundleLimitedQuantity {
        personalLimit: number;
    }

    type BundleRestrictInteractionDateTimeValues = {
        purchasableFrom: string;
        purchasableTill: string;
        description: Nullable<string>;
    };

    type BundleRestrictInteractionTokenValues = {
        description: string;
    };

    interface IBundleRestrictInteraction {
        type: BundleRestrictInteractionType;
        condition: UserPrerequisiteCondition;
        values: BundleRestrictInteractionDateTimeValues | BundleRestrictInteractionTokenValues;
    }

    interface IUserPrerequisite {
        tags: UserPrerequisiteTags[];
        strategies: {
            restrictInteraction?: IBundleRestrictInteraction;
        };
    }

    interface IBundlePrice {
        value?: number;
        currency?: string;
        priceWithQuantity?: number;
    }

    type SIZE_IN_GRID = '1/4' | '1/3' | '1/2' | '3/4' | '2/3' | '1';

    type IQuantityData = Partial<{
        quantity: number;
        initial: Record<string, number>;
        withDiscount: Record<string, number>;
        final: Record<string, number>;
        lack: Record<string, number>;
    }>;

    type WsmartProductForArmory = Pick<
        ProductEntity,
        | 'price'
        | 'formattedPrice'
        | 'formattedOriginalPrice'
        | 'formattedDiscount'
        | 'currency'
        | 'id'
        | 'entitlements'
        | 'discount'
        | 'originalPrice'
        | 'promoTimerActiveTill'
        | 'availablePersonalCount'
        | 'promoLabels'
    >;

    interface IBundlePromoTimer {
        activeFrom: Nullable<string>;
        activeTill: Nullable<string>;
        showCountdown: boolean;
        description: Nullable<string>;
    }

    interface IBundle {
        name: string;
        id: number;
        parentBundleId: Nullable<number>;
        type: BUNDLE_TYPES;
        subType: BUNDLE_TYPES;
        categories?: string[];

        price?: Nullable<string | number>;
        currency?: Nullable<string>;
        additionalPrice?: Nullable<string | number>;
        additionalCurrency?: Nullable<string>;

        discount?: Nullable<number>;
        showDiscountLabel: boolean;
        originalPrice?: Nullable<number | string>;
        allowCompensation: boolean;

        labelNewActiveTill: Nullable<string>;

        title: string;
        normalizedTitle: Nullable<string>;
        description?: Nullable<string>;
        descriptionAdditional?: Nullable<string>;
        descriptionBrief?: string;

        videoBackground: Nullable<IVideo>;
        entryVideo: IVideo;
        bundleCardHoverVideo: Nullable<IVideo>;
        bundleCardIdleVideo: Nullable<IVideo>;

        bundleContentUnderlayColor: Nullable<string>;

        audioTrack: Nullable<IAudioTrack>;
        audioHover: Nullable<IAudioTrack>;

        hideAdultCheck: boolean;

        icons: BundleIcons | RandomBundleIcons;

        backgroundColor: Nullable<string>;

        filterValues?: any;

        sizeInGrid: Nullable<SIZE_IN_GRID>;
        decoration: BUNDLE_DECORATION[];
        denyTitleModification: boolean;

        limitedQuantity?: Nullable<IBundleLimitedQuantity>;
        singlePurchaseMaxQuantity: Nullable<number>;

        promoTimer: IBundlePromoTimer;

        disablePortPreview: boolean;

        quantityData: IQuantityData;

        serialPurchase: boolean;
        dependentBundle: number;
        nextBundle: number;
        serialIndex: number;
        serialSequence: number[];
        isValuableSerialBundle: Nullable<boolean>;
        enableMultiplePurchase: Nullable<boolean>;
        isHidden: boolean;

        randomIsInfinite: boolean;
        randomIsTotalCountHidden: boolean;
        randomIsSpecial: boolean;
        randomPurchaseCooldown: number;

        isShiny: boolean;

        groupName: string;
        groupIndex: number;

        randomShowCarusel: boolean;

        gallery?: Nullable<IGalleryItem[]>;
        isFullscreenGallery: boolean;

        randomBundleChildren: IRandomBundleChild[];

        primaryId: Nullable<number>;
        primaryItem: Nullable<BundlePrimaryType>;
        entitlements?: Nullable<IBundleEntity[]>;

        autoDescriptionSettings?: Nullable<IAutoDescriptionSettings>;

        hideAdditionalContent: Nullable<string>;

        progressivePurchase: Nullable<ProgressivePurchaseSettings>;

        randomBundleShinyAnimation: Nullable<RandomBundleShinyAnimationType>;

        randomHideEqualProbabilityNotice: Nullable<boolean>;

        promoLabel: Nullable<string>;

        isPurchased?: boolean;
        isRandom?: boolean;

        purchaseType: BundlePurchaseTypes;
        productCode: Nullable<string>;
        productId: Nullable<string>;
        isLoading?: boolean;
        originalProduct?: ProductEntity;

        disclaimer: Nullable<string>;

        isInitialized?: boolean;
        userPrerequisite?: IUserPrerequisite;
    }

    type DiscountDiapason = {
        discount: number;
        price: number;
        diapason: number[];
    };

    type PartialPurchaseSettings = {
        isEnabled?: boolean;
        restrictions?: Nullable<IRestriction[]>;
    };

    type WholePurchaseSettings = {
        isEnabled?: boolean;
        restrictions?: Nullable<IRestriction[]>;
    };

    type ProgressivePurchaseSettings = {
        partialPurchase?: Nullable<PartialPurchaseSettings>;
        wholeBundlePurchase?: Nullable<WholePurchaseSettings>;
        discountDiapasons: DiscountDiapason[];
    };

    type BundlePrimaryType = IBundleEntity & {
        filterValues?: IPrimaryItemCustomisation;
    };

    type RandomBundleShinyAnimationType = {
        isEnabled?: Nullable<boolean>;
        image?: Nullable<string>;
    };

    interface ILootboxStateItemFiller {
        amount: number;
        type: string;
        id: Nullable<number>;
    }

    interface ILootboxStateItemGroupRewards<T> {
        [key: string]: T;
    }

    interface ILootboxIRewardIcons {
        medium: Nullable<string>;
        large: Nullable<string>;
        small: Nullable<string>;
        default: Nullable<string>;
    }

    type LootboxRewardShipAdditionalData = {
        id: string;
        level: number;
        nation: {
            name: string;
            icons: ILootboxIRewardIcons;
        };
        icons: ILootboxIRewardIcons;
        type: {
            name: string;
            icons: ILootboxIRewardIcons;
        };
        title: string;
        isSpecial: boolean;
        isPremium: boolean;
        defaultCrew: Nullable<LootboxRewardCrewAdditionalData>;
    };

    type LootboxRewardMultiBoostAdditionalData = {
        title: string;
        icons: ILootboxIRewardIcons;
        restrictions: {
            levels: number[];
        };
    };

    type LootboxRewardCamoAdditionalData = {
        title: string;
        isNative: Nullable<boolean>;
        icons: ILootboxIRewardIcons;
        type: Nullable<string>;
        subType: Nullable<string>;
        ship: Nullable<LootboxRewardShipAdditionalData>;
    };

    type LootboxRewardCrewAdditionalData = {
        title: string;
        nation: {
            name: string;
            icons: ILootboxIRewardIcons;
        };
        type: string;
        isUnique: boolean;
        icons: ILootboxIRewardIcons;
        hasSampleVo: boolean;
    };

    type LootboxRewardSignalAdditionalData = {
        title: string;
        tags: string[];
    };

    type LootboxRewardAdditionalData = LootboxRewardShipAdditionalData &
        LootboxRewardCamoAdditionalData &
        LootboxRewardCrewAdditionalData &
        LootboxRewardSignalAdditionalData &
        LootboxRewardMultiBoostAdditionalData;

    interface ILootboxReward {
        probability: string;
        weight: number;
        probabilityDisplayed: number;
        amount: number;
        type: string;
        defaultCrew: Nullable<number>;
        id: Nullable<number>;
        shipId: Nullable<number>;
        crewLevel: Nullable<number>;
        additionalData: LootboxRewardAdditionalData;
    }

    interface ILootboxRewardGroup {
        title: Nullable<string>;
        shortTitle: Nullable<string>;
        description: Nullable<string>;
        probabilityDisplayed: number;
        display: Nullable<ILootboxRewardGroupDisplay>;
        rerollNonUniqueCrews: boolean;
        rewards: ILootboxReward[];
        savePoint: Nullable<number>;
        commonRewardType: Nullable<CommonRewardType>;
        hasUniqueRewards: boolean;
        isOnlyCrewsValuableRewards: boolean;
    }

    type ILootboxStateRewards = ILootboxStateItemGroupRewards<ILootboxRewardGroup>;

    interface ILootboxStateItemSlot {
        title: Nullable<string>;
        shortDescription: Nullable<string>;
        fullDescription: Nullable<string>;
        icon: Nullable<string>;

        continuousRewards: Nullable<boolean>;
        commonRewards: ILootboxStateItemGroupRewards<ILootboxRewardGroup>;
        valuableRewards: ILootboxStateItemGroupRewards<ILootboxRewardGroup>;
    }

    interface ILootboxPreviewSettingsItemReward {
        image: Nullable<string>;
        title: Nullable<string>;
        type: PreviewRewardCustomTypes;
        id: Nullable<number>;
        amount: Nullable<number>;
    }

    interface ILootboxPreviewSettingsDataItem {
        slotIndex: number;
        rewards: ILootboxPreviewSettingsItemReward[];
    }

    type ILootboxPreviewSettingsData = ILootboxPreviewSettingsDataItem[];

    interface ILootboxPreviewSettings {
        template: PreviewSettingsTemplate;
        data: Nullable<ILootboxPreviewSettingsData>;
    }

    interface ILootboxSavePointForValuableGroup {
        savePoint: number;
        groupName: string;
        groupTitle: string;
        isUniqueGroup: boolean;
    }

    interface ILootboxStateItem {
        id: number;
        title: Nullable<string>;
        shortTitle: Nullable<string>;
        isPremium: boolean;
        filler: Nullable<ILootboxStateItemFiller>;
        savePoint: number;
        savePointForValuableGroup: Nullable<ILootboxSavePointForValuableGroup>;
        slots: ILootboxStateItemSlot[];
        hasValuableRewards: Nullable<boolean>;
        uniqueGroupTitles: string[];
    }

    interface ILootboxes {
        [key: number]: ILootboxStateItem;
    }

    interface ILotboxPromotionRewards {
        groupIndex: number;
        slotIndex: number;
        sortOrder: number[];
    }

    type LootboxPromotionRewardsInsideSlots = Nullable<ILotboxPromotionRewards[]>;
    type LootboxPreviewSettings = Nullable<ILootboxPreviewSettings>;

    interface ILootboxAutoDescriptionSettings {
        id: number;
        isDisabled: Nullable<boolean>;
        isDroprateHidden: Nullable<boolean>;
        title: Nullable<string>;
        description: Nullable<string>;
        previewSettings: LootboxPreviewSettings;
        promotionRewardsInsideSlots: LootboxPromotionRewardsInsideSlots;
        isGroupedCommonRewards: Nullable<boolean>;
        openPortByItemParams: Nullable<Omit<IOpenPortByItem, 'item' | 'bundleId'>>;
    }

    interface ILootboxAutoDescriptionSettingsMap {
        [key: number]: ILootboxAutoDescriptionSettings;
    }

    interface IAutoDescriptionSettings {
        isDisabled: Nullable<boolean>;
        lootboxSettings: Nullable<ILootboxAutoDescriptionSettings[] | ILootboxAutoDescriptionSettingsMap>;
    }

    interface IRequestGift {
        data: IGift[];
        meta: {
            total: number;
        };
    }

    interface IGift {
        name: string;
        id: number;
        title: string;
        description: string;
        isObtained: boolean;
        icons: DefaultBundleIcons;
        entryVideo: IVideo;
        decoration: string[];
        offersLimit: number;
        activeTill: string;
        activeFrom: string;
        primaryId: number;
        entitlements: IBundleEntity[];
        isSecretGift: boolean;
        audioTrack: Nullable<IAudioTrack>;
    }

    interface IFeatureItem {
        category: Nullable<string>;
        description: Nullable<string>;
        title: Nullable<string>;
        key: Nullable<string>;
    }

    interface IFeatureCategoryIcons {
        default: string;
        localDefault: string;
    }

    interface IFeatureCategory {
        icons: IFeatureCategoryIcons;
        title: Nullable<string>;
    }

    interface IFeatureCategories {
        category: Nullable<IFeatureCategory>;
        features: Nullable<IFeatureItem[]>;
    }

    interface IFeaturesItem {
        id: number;
        mainFeatures: Nullable<IFeatureItem[]>;
        categories: Nullable<IFeatureCategories[]>;
    }

    type ShipFeatureMap = Record<string, IFeaturesItem>;
}

export default global;
